import { NavLink } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';

import {
  CurateIcon,
  HomeIcon,
  LensIcon,
} from '@/components/ui/Icon';
import { ROUTES } from '@/routes/paths';

import styles from './SideNav.module.css';

interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * When true, the nav item is active for the exact `to` path only. When
   * false, the item also lights up for any nested subroute (e.g. /lens/rsq
   * keeps the "Lens" rail active). Default true.
   */
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.dashboard, label: 'Home', Icon: HomeIcon, exact: true },
  // Lens redirects to /lens/rsq, so use prefix matching so the rail stays lit.
  { to: ROUTES.lens, label: 'Lens', Icon: LensIcon, exact: false },
  { to: ROUTES.curate, label: 'Curate', Icon: CurateIcon, exact: true },
];

/**
 * Slim icon-rail navigation shown on >=900px viewports. The brand mark lives
 * in the top Header now, so this rail is purely the primary-route launcher.
 * Each item is an icon button with a visually-hidden text label preserved for
 * accessibility and rendered as a tooltip on hover.
 */
export function SideNav() {
  return (
    <aside className={styles.sidebar} aria-label="Primary navigation">
      <nav className={styles.nav}>
        {NAV_ITEMS.map(({ to, label, Icon, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact !== false}
            title={label}
            className={({ isActive }) =>
              isActive ? `${styles.item} ${styles.itemActive}` : styles.item
            }
          >
            <span className={styles.itemIcon} aria-hidden="true">
              <Icon width={22} height={22} />
            </span>
            <span className={styles.itemLabel}>{label}</span>
            <span className={styles.tooltip} role="tooltip">{label}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
